import { apiRequest } from './http';
import type { NetworkMetaWriteResult, ResellerRefreshStatus } from './networkMeta';

// 리셀러 허용 목록 API — BigQuery 정본 resellers_project_staging.reseller_allowed_source 를 다룬다.
// 이메일당 1행이고, Dagster reseller_allowed_job 이 이걸 (이메일, 리셀러 이름) 2컬럼으로 펼친다.
// 추가·수정·삭제는 바로 반영되지 않고 요청함(reseller_allowed_apply)으로 접수된다 — admin 도 같다.
// 승인되면 서버가 정본에 반영하고 재생성을 건다(Admon 네트워크와 같은 흐름).

export interface ResellerAllowedRow {
  // 서버가 TRIM + LOWER 로 저장한 키.
  reseller_email: string;
  // true 면 GAM 매핑의 리셀러 전체를 본다 — 이때 reseller_names 는 빈 배열이다.
  full_access: boolean;
  reseller_names: string[];
  note: string | null;
  // 낙관적 잠금 토큰 — 수정·삭제 때 그대로 돌려보낸다.
  updated_at: string;
  updated_by: string | null;
}

export interface ResellerAllowedListResponse {
  rows: ResellerAllowedRow[];
  // 고를 수 있는 리셀러 이름 — Admon 네트워크에서 GAM 코드가 있는 sc_naming(= GAM 매핑과 같은 집합).
  reseller_options: string[];
  // 승인 대기(pending) 중인 반영 요청 — 오래된 순. 화면의 '요청 대기' 표시가 쓴다.
  pending_requests: ResellerAllowedPendingRequest[];
  generated_at: string;
}

// 승인 대기 요청 1건. 이메일만 담겨 있고 고칠 리셀러 이름은 없다(요청 상세에서 본다).
export interface ResellerAllowedPendingRequest {
  request_id: number;
  kind: ResellerAllowedRequestPayload['kind'];
  // bulk_create 면 여러 개, 나머지는 1개.
  reseller_emails: string[];
  created_at: string;
  requester_email: string | null;
}

export interface ResellerAllowedSaveBody {
  full_access: boolean;
  reseller_names: string[];
  note: string | null;
  // null 이면 새로 만들기(이미 있으면 409), 값이 있으면 그 토큰으로 수정(다르면 409).
  expected_updated_at: string | null;
  // 변경 사유 — 요청의 reason 으로 들어간다.
  reason: string | null;
}

// POST /api/reseller-allowed/bulk body — 여러 이메일을 같은 보는 범위·메모로 한 요청에 만든다.
export interface ResellerAllowedBulkCreateBody {
  reseller_emails: string[];
  full_access: boolean;
  reseller_names: string[];
  note: string | null;
  reason: string | null;
}

// 한 요청에 담을 수 있는 이메일 수(정규화·중복 제거 뒤) — 서버 BULK_CREATE_LIMIT 과 같다.
export const RESELLER_BULK_LIMIT = 200;

// 요청 상세가 읽는 access_requests.payload(request_type='reseller_allowed_apply').
// before 는 서버가 접수 때 읽은 행이다(create·bulk_create 면 null), after 는 반영할 값이다(delete 면 null).
// bulk_create 면 reseller_email 이 null 이고 대상은 reseller_emails 다(나머지는 빈 배열).
export interface ResellerAllowedRequestPayload {
  kind: 'create' | 'update' | 'delete' | 'bulk_create';
  reseller_email: string | null;
  reseller_emails: string[];
  before: ResellerAllowedRow | null;
  after: { full_access: boolean; reseller_names: string[]; note: string | null } | null;
  expected_updated_at: string | null;
}

// 경로 키는 서버와 같은 규칙으로 정규화한다 — 고친 내용 비교와 요청 대상이 서버 키와 같게.
export function normalizeResellerEmail(email: string): string {
  return email.trim().toLowerCase();
}

// 서버 형식 검사(models._normalize_email)와 같은 규칙 — 정규화한 값에 쓴다.
export function isResellerEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// GET /api/reseller-allowed — 전 행 + 이름 후보 + 승인 대기 요청. 정렬·검색·묶기는 클라이언트가 한다.
export function fetchResellerAllowed(signal?: AbortSignal): Promise<ResellerAllowedListResponse> {
  return apiRequest<ResellerAllowedListResponse>('/reseller-allowed', { signal });
}

// PUT /api/reseller-allowed/{email} — 만들기와 수정 요청을 함께 접수한다. 409 면 그 사이 다른 저장이 있었다.
// 응답은 접수 결과(request_id)다 — 승인되기 전까지 목록은 그대로다.
export function saveResellerAllowed(email: string, body: ResellerAllowedSaveBody): Promise<NetworkMetaWriteResult> {
  return apiRequest<NetworkMetaWriteResult>(
    `/reseller-allowed/${encodeURIComponent(normalizeResellerEmail(email))}`,
    { method: 'PUT', body },
  );
}

// POST /api/reseller-allowed/bulk — 여러 이메일 추가 요청 1건 접수. 응답은 PUT 과 같다.
// 이미 등록된 이메일이 있으면 409 이고 detail 이 {message, existing_emails} 객체다.
export function bulkCreateResellerAllowed(body: ResellerAllowedBulkCreateBody): Promise<NetworkMetaWriteResult> {
  return apiRequest<NetworkMetaWriteResult>('/reseller-allowed/bulk', { method: 'POST', body });
}

// DELETE /api/reseller-allowed/{email}?expected_updated_at=...&reason=... — 삭제 요청 접수. 응답은 PUT 과 같다.
// 토큰이 다르면 409, 없으면 404.
export function deleteResellerAllowed(
  email: string,
  expectedUpdatedAt: string,
  reason: string | null,
): Promise<NetworkMetaWriteResult> {
  return apiRequest<NetworkMetaWriteResult>(
    `/reseller-allowed/${encodeURIComponent(normalizeResellerEmail(email))}`,
    { method: 'DELETE', query: { expected_updated_at: expectedUpdatedAt, reason } },
  );
}

// ── 리셀러 허용 재생성(reseller_allowed_job) ───────────────────
// 요청이 승인·반영되면 서버가 자동으로 건다. 이 호출은 자동 실행이 실패했을 때 요청 상세에서 같은 재생성을
// 다시 시작한다(edit_direct 만).

export function fetchResellerAllowedRefreshStatus(signal?: AbortSignal): Promise<ResellerRefreshStatus> {
  return apiRequest<ResellerRefreshStatus>('/reseller-allowed/refresh', { signal });
}

// 202 로 바로 돌아온다 — 결과는 상태 조회의 최근 이벤트로 본다. 꺼져 있으면 503, 실행 중이면 409.
// requestId 를 주면 그 승인 요청의 재실행이라 이벤트가 요청 상세에 남는다.
export function startResellerAllowedRefresh(requestId?: number): Promise<{ started: boolean; dry_run: boolean }> {
  return apiRequest<{ started: boolean; dry_run: boolean }>('/reseller-allowed/refresh', {
    method: 'POST',
    body: requestId === undefined ? {} : { request_id: requestId },
  });
}
